/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { Badge, Box, Flex, HStack, Text, Icon, Button } from '@chakra-ui/react';
import { FaUsers } from 'react-icons/fa';
import {
  capitalizeFirstLetter,
  formatVariantSchedule,
  getVariantPrice,
} from '../../utils';
import { seatsLeftLabel } from './activityCheckout';

const RadioCard = ({
  isSelected,
  onClick,
  children,
  borderBottomLeftRadius,
  borderBottomRightRadius,
}) => {
  return (
    <Flex
      as="label"
      justify="center"
      cursor="pointer"
      fontSize="sm"
      borderWidth="1px"
      borderBottomLeftRadius={borderBottomLeftRadius}
      borderBottomRightRadius={borderBottomRightRadius}
      height="36px"
      align="center"
      onClick={onClick}
      padding="0 16px"
      boxShadow="md"
      width="100%"
      flex={1}
      bg={isSelected ? 'teal.600' : 'white'}
      color={isSelected ? 'white' : 'inherit'}
      borderColor={isSelected ? 'teal.600' : 'gray.200'}
      _focus={{
        boxShadow: 'outline',
      }}
    >
      {children}
    </Flex>
  );
};

const EventVariantItem = ({
  applyDiscount,
  event,
  variante,
  isSelected,
  selectedVariantPriceOptions,
  handlePriceOptionChange,
  isAlreadyPurchased = false,
}) => {
  const { Sconto_Fratelli: scontoFratelli, accontoAmount } = event;

  const [priceOption, setPriceOption] = useState(
    selectedVariantPriceOptions[variante.documentId]?.priceOption || ''
  );

  // Calcola i posti disponibili per questa variante
  const calculateAvailableSeats = () => {
    const variantId = variante?.documentId;

    // Preferiamo gli ordini attaccati alla variante; se non ci sono, fallback agli ordini globali dell'evento.
    const variantOrders = Array.isArray(variante?.Ordini)
      ? variante.Ordini
      : [];
    const eventOrders = Array.isArray(event?.orders)
      ? event.orders
      : Array.isArray(event?.Orders)
        ? event.Orders
        : [];
    const sourceOrders = variantOrders.length > 0 ? variantOrders : eventOrders;

    const activeVariantOrders = sourceOrders.filter((order) => {
      const products = Array.isArray(order?.metadata?.products)
        ? order.metadata.products
        : [];

      const relevantActiveProducts = products.filter(
        (product) =>
          product &&
          ['acconto', 'totale'].includes(product.paymentType) &&
          product.id === variantId &&
          product.isCancelled !== true
      );

      return relevantActiveProducts.length > 0;
    });

    const uniqueSocios = new Set();
    activeVariantOrders.forEach((order) => {
      const socioId = order?.socio?.documentId || order?.socio?.email;
      if (socioId) uniqueSocios.add(socioId);
    });

    const hasSocioData = activeVariantOrders.some(
      (order) => order?.socio?.documentId || order?.socio?.email
    );

    const occupied = hasSocioData
      ? uniqueSocios.size
      : activeVariantOrders.length;
    const capienza = variante?.Capienza;

    // Se non c'è capienza definita, considera sempre disponibile (come EventVariants).
    if (!capienza && capienza !== 0) return 999;

    return Math.max(0, capienza - occupied);
  };

  const availableSeats = calculateAvailableSeats();
  const isSoldOut = availableSeats !== 999 && availableSeats <= 0;

  // Calcola il prezzo della variante
  const computeVariantPrice = () => {
    const basePrice = getVariantPrice(variante, event);

    if (priceOption === 'acconto' && accontoAmount) {
      return accontoAmount;
    }

    if (applyDiscount && scontoFratelli) {
      return basePrice - (parseFloat(scontoFratelli) || 0);
    }

    return basePrice;
  };

  const variantPrice = computeVariantPrice();

  const formatVariantDate = () => {
    const { date, time } = formatVariantSchedule(variante);
    if (!date) return 'Data non definita';
    return time ? `${date} · ${time}` : date;
  };

  const handleInternalPriceOptionChange = (value) => {
    if (isAlreadyPurchased || isSoldOut) {
      return;
    }

    setPriceOption(value);

    if (priceOption === value) {
      handlePriceOptionChange({
        applyDiscount,
        variantId: variante.documentId,
        name: variante.Nome,
        priceOption: '',
      });
      return;
    }

    handlePriceOptionChange({
      applyDiscount,
      variantId: variante.documentId,
      name: variante.Nome,
      priceOption: value,
    });
  };

  const handleVariantClick = () => {
    if (isAlreadyPurchased || isSoldOut) return;

    if (accontoAmount) {
      if (isSelected) {
        handleInternalPriceOptionChange('');
      }
      return;
    }

    if (isSelected) {
      handleInternalPriceOptionChange('');
    } else {
      handleInternalPriceOptionChange('totale');
    }
  };

  const handleCardSelectTotale = () => {
    if (isAlreadyPurchased || isSoldOut || accontoAmount || isSelected) return;
    handleInternalPriceOptionChange('totale');
  };

  useEffect(() => {
    setPriceOption(
      selectedVariantPriceOptions[variante.documentId]?.priceOption || ''
    );
  }, [selectedVariantPriceOptions]);

  const selectedOptionForVariant =
    selectedVariantPriceOptions[variante.documentId]?.priceOption;
  const seatsLabel = seatsLeftLabel(availableSeats);

  useEffect(() => {
    if (isSoldOut && selectedOptionForVariant) {
      handlePriceOptionChange({
        applyDiscount,
        variantId: variante.documentId,
        name: variante.Nome,
        priceOption: '',
      });
    }
  }, [
    isSoldOut,
    selectedOptionForVariant,
    applyDiscount,
    variante.documentId,
    variante.Nome,
    handlePriceOptionChange,
  ]);

  return (
    <Box
      key={variante.documentId}
      borderRadius="lg"
      borderWidth="2px"
      boxShadow="lg"
      width="100%"
      position="relative"
      transition="transform 0.2s"
      textAlign="left"
      px={4}
      pt={4}
      pb={3}
      cursor="default"
      borderColor={
        isAlreadyPurchased || isSoldOut
          ? 'gray.200'
          : isSelected
            ? 'blue.400'
            : 'gray.200'
      }
      bg={
        isAlreadyPurchased
          ? 'gray.100'
          : isSoldOut
            ? 'gray.50'
            : isSelected && !applyDiscount
              ? 'blue.50'
              : applyDiscount
                ? 'green.50'
                : 'white'
      }
      opacity={isAlreadyPurchased ? 0.7 : isSoldOut ? 0.85 : 1}
    >
      {applyDiscount && !isSoldOut && (
        <Badge
          colorScheme="red"
          fontSize="xs"
          px={1.5}
          py={0.5}
          borderRadius="sm"
          borderTopLeftRadius={0}
          borderBottomRightRadius={0}
          boxShadow="sm"
          position="absolute"
          top={0}
          right={0}
        >
          -€{scontoFratelli}
        </Badge>
      )}

      {isSoldOut && !isAlreadyPurchased && (
        <Badge
          colorScheme="red"
          fontSize="xs"
          px={1.5}
          py={0.5}
          borderRadius="sm"
          borderTopRightRadius={0}
          borderBottomLeftRadius={0}
          boxShadow="sm"
          position="absolute"
          top={0}
          left={0}
        >
          Sold out
        </Badge>
      )}

      {/* Badge per varianti già acquistate */}
      {isAlreadyPurchased && (
        <Badge
          colorScheme="blue"
          fontSize="xs"
          px={1.5}
          py={0.5}
          borderRadius="sm"
          borderTopRightRadius={0}
          borderBottomLeftRadius={0}
          boxShadow="sm"
          position="absolute"
          top={0}
          left={0}
          color="white"
          bg="blue.500"
        >
          ✓ Acquistato
        </Badge>
      )}

      <Box
        mb={3}
        onClick={handleCardSelectTotale}
        onKeyDown={(e) => {
          if (
            (e.key === 'Enter' || e.key === ' ') &&
            !isAlreadyPurchased &&
            !isSoldOut &&
            !accontoAmount &&
            !isSelected
          ) {
            e.preventDefault();
            handleCardSelectTotale();
          }
        }}
        cursor={
          !isAlreadyPurchased && !isSoldOut && !accontoAmount && !isSelected
            ? 'pointer'
            : 'default'
        }
        role={
          !isAlreadyPurchased && !isSoldOut && !accontoAmount && !isSelected
            ? 'button'
            : undefined
        }
        tabIndex={
          !isAlreadyPurchased && !isSoldOut && !accontoAmount && !isSelected
            ? 0
            : undefined
        }
      >
        <Text
          fontSize="md"
          color={
            isAlreadyPurchased
              ? 'gray.500'
              : isSoldOut
                ? 'gray.600'
                : 'gray.800'
          }
          fontWeight="600"
          mb={2}
          minH="unset"
        >
          {variante.Nome}
        </Text>

        <Text
          color={
            isAlreadyPurchased
              ? 'gray.400'
              : isSoldOut
                ? 'gray.500'
                : 'gray.600'
          }
          fontSize="xs"
          mb={2}
        >
          {formatVariantDate()}
        </Text>

        <Flex justify="space-between" align="baseline" gap={3}>
          {!isSoldOut && (
            <Text
              color={isAlreadyPurchased ? 'gray.400' : 'green.600'}
              fontSize="sm"
              fontWeight="bold"
            >
              {variantPrice === 0 || variantPrice === null
                ? 'Gratis'
                : `€${variantPrice}`}
            </Text>
          )}

          {seatsLabel ? (
            <HStack spacing={1} flexShrink={0}>
              <Icon
                as={FaUsers}
                color={
                  isAlreadyPurchased
                    ? 'gray.400'
                    : isSoldOut
                      ? 'red.400'
                      : 'purple.500'
                }
                boxSize="14px"
              />
              <Text
                color={
                  isAlreadyPurchased
                    ? 'gray.400'
                    : isSoldOut
                      ? 'red.500'
                      : 'purple.600'
                }
                fontSize="sm"
                fontWeight="bold"
              >
                {seatsLabel}
              </Text>
            </HStack>
          ) : null}
        </Flex>

        {/* Descrizione della variante */}
        {variante.Descrizione && (
          <Text
            color={isAlreadyPurchased ? 'gray.400' : 'gray.600'}
            fontSize="xs"
            mt={2}
            px={2}
            lineHeight="1.4"
            whiteSpace="pre-wrap"
          >
            {variante.Descrizione}
          </Text>
        )}

        {!isAlreadyPurchased && !isSoldOut && isSelected && (
          <Button
            mt={2}
            size="sm"
            colorScheme="green"
            variant="solid"
            width="100%"
            onClick={(e) => {
              e.stopPropagation();
              handleVariantClick();
            }}
          >
            ✓ Selezionata
          </Button>
        )}
      </Box>
      {Boolean(accontoAmount) && !isAlreadyPurchased && !isSoldOut && (
        <HStack
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={2}
          gap={0}
        >
          {['acconto', 'totale'].map((value, index) => (
            <RadioCard
              key={value}
              borderBottomLeftRadius={index === 0 ? 'md' : '0'}
              borderBottomRightRadius={index === 1 ? 'md' : '0'}
              isSelected={priceOption === value}
              onClick={() => handleInternalPriceOptionChange(value)}
            >
              {capitalizeFirstLetter(value)}
            </RadioCard>
          ))}
        </HStack>
      )}
    </Box>
  );
};

export default EventVariantItem;
